import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import {
  useTeamMembers,
  useInviteTeamMember,
  useUpdateTeamMemberRole,
  useRemoveTeamMember,
} from '../../services/settings';
import { errorMessageFrom } from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import InviteMemberModal from './InviteMemberModal';
import type { TeamMember } from '../../types/settings';
import type { UserRole } from '../../types/auth';

/**
 * Settings → Team: staff management for the two-role model (product
 * decision 2026-07-14 — Admin and Trainer only; no Owner tier).
 *
 * - Lists staff memberships (learners live under /learners, not here).
 * - Invite by email with a role; change a member's role; remove a member.
 * - The backend enforces the safety net: the last Admin of a workspace can
 *   never be demoted or removed.
 * - Legacy 'Owner' rows (pre-migration workspaces) render as Admin.
 */

const STAFF_ROLE_OPTIONS: UserRole[] = ['Admin', 'Trainer'];

/** Roles that belong on this screen (everything that isn't a pure learner). */
const isStaffRole = (role: string) => role !== 'Member' && role !== 'Learner';

/** Legacy Owner displays (and is treated) as Admin in the two-role model. */
const displayRole = (role: string): string => (role === 'Owner' ? 'Admin' : role);

const roleBadgeClass = (role: string) =>
  displayRole(role) === 'Admin'
    ? 'bg-primary-500/10 text-primary-700 dark:text-primary-350'
    : 'bg-secondary-500/10 text-secondary-700 dark:text-secondary-300';

const initialsOf = (m: TeamMember) =>
  `${m.first_name?.[0] ?? ''}${m.last_name?.[0] ?? ''}`.toUpperCase() || m.email[0].toUpperCase();

const TeamMembers: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading } = useTeamMembers({ limit: 100 });
  const inviteMember = useInviteTeamMember();
  const updateRole = useUpdateTeamMemberRole();
  const removeMember = useRemoveTeamMember();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<TeamMember | null>(null);

  const staff = (data?.members ?? []).filter((m) => isStaffRole(m.role));

  const handleInvite = async (email: string, role: string) => {
    await inviteMember.mutateAsync({ email, role: role as UserRole });
    toast.success(`Invitation sent to ${email}`);
    setInviteOpen(false);
  };

  const handleRoleChange = (member: TeamMember, role: string) => {
    updateRole.mutate(
      { userId: member.user_id, data: { role: role as Exclude<UserRole, 'Owner'> } },
      {
        onSuccess: () => toast.success(`${member.first_name || member.email} is now ${role}`),
        onError: (err) =>
          toast.error(errorMessageFrom(err) ?? 'Could not change the role. Please try again.'),
      }
    );
  };

  const handleRemove = () => {
    if (!removing) return;
    removeMember.mutate(removing.user_id, {
      onSuccess: () => {
        toast.success(`${removing.first_name || removing.email} removed from the workspace`);
        setRemoving(null);
      },
      onError: (err) => {
        toast.error(errorMessageFrom(err) ?? 'Could not remove this member. Please try again.');
        setRemoving(null);
      },
    });
  };

  return (
    <section className="max-w-3xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-text-primary">Team</h2>
          <p className="mt-1 text-sm text-text-secondary">
            The people who run this workspace. Learners are managed under Learner Management.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold transition-colors duration-fast focus-ring"
        >
          <UserPlusIcon className="w-4 h-4" aria-hidden="true" />
          Invite member
        </button>
      </header>

      <div className="bg-surface-primary rounded-2xl shadow-card overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-sm text-text-secondary" aria-busy="true">
            Loading team…
          </p>
        ) : staff.length === 0 ? (
          <p className="p-6 text-sm text-text-secondary">No team members yet.</p>
        ) : (
          <ul className="divide-y divide-border-primary/15" data-testid="team-list">
            {staff.map((member) => {
              const isSelf = member.user_id === user?.id;
              return (
                <li
                  key={member.user_id}
                  className="flex items-center gap-4 px-5 py-4"
                  data-testid={`team-row-${member.user_id}`}
                >
                  <span className="shrink-0 w-10 h-10 rounded-full bg-primary-200 dark:bg-primary-800 flex items-center justify-center text-sm font-bold text-primary-700 dark:text-primary-350">
                    {initialsOf(member)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {`${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() ||
                        member.email}
                      {isSelf && <span className="ml-1.5 text-xs text-text-tertiary">(you)</span>}
                    </p>
                    <p className="text-xs text-text-secondary truncate">{member.email}</p>
                  </div>

                  {isSelf ? (
                    <span
                      className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${roleBadgeClass(member.role)}`}
                    >
                      {displayRole(member.role)}
                    </span>
                  ) : (
                    <>
                      <select
                        value={displayRole(member.role)}
                        onChange={(e) => handleRoleChange(member, e.target.value)}
                        disabled={updateRole.isPending}
                        aria-label={`Role of ${member.email}`}
                        className="shrink-0 rounded-lg border border-border-primary/40 bg-surface-primary px-2.5 py-1.5 text-sm text-text-primary focus-ring disabled:opacity-50"
                      >
                        {STAFF_ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setRemoving(member)}
                        disabled={removeMember.isPending}
                        className="shrink-0 text-sm font-medium text-error-600 dark:text-error-400 hover:underline disabled:opacity-50 focus-ring rounded px-1"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {inviteOpen && (
        <InviteMemberModal onClose={() => setInviteOpen(false)} onInvite={handleInvite} />
      )}

      {removing && (
        <ConfirmDialog
          title="Remove team member?"
          message={`${removing.first_name || removing.email} will lose access to this workspace. Their courses and content stay.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          onConfirm={handleRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  );
};

export default TeamMembers;
